// ── V149 → V154: 오늘의 작업 인쇄 — 주방에 붙이는 작업표 (한국어 위, 중국어 아래) ──────────
// 태블릿을 못 쓰는 직원이 종이에 체크하고 시간·이름을 적는다.
// recipePrint.js와 같은 원칙: 원가·가격은 찍지 않는다. 중국어가 없으면 한국어만.
import { zhOf } from '../i18n/BiName';
import { scaleIngredients, fmtAmount, fmtDay, recipeBase } from './workCalc';
import { fmtDue } from './workMeta';   // V155 내용 · 마감 · 담당자 (V156 회의 테스크 표시)

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const hhmm = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const css = (landscape) => `
  @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 10mm 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #111;
         font-family: "Noto Sans KR","Malgun Gothic","Apple SD Gothic Neo",
                      "Noto Sans SC","PingFang SC","Microsoft YaHei",sans-serif; }
  .bar { position: sticky; top: 0; background: #18181B; color: #fff; padding: 10px 16px;
         display: flex; justify-content: space-between; align-items: center; font-size: 14px; }
  .bar button { background: #FF6F0F; color: #fff; border: 0; border-radius: 8px;
                padding: 8px 16px; font-size: 14px; font-weight: 700; cursor: pointer; }
  main { padding: 14px 6px; }
  header { border-bottom: 3px solid #111; padding-bottom: 8px; margin-bottom: 10px; display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
  h1 { font-size: 28px; margin: 0; }
  h1 span { color: #1A56A0; font-size: 22px; margin-left: 8px; }
  .meta { font-size: 15px; color: #444; }
  table.wt { width: 100%; border-collapse: collapse; }
  table.wt th { font-size: 13px; color: #333; border: 1.5px solid #111; background: #f2f2f2; padding: 5px 6px; text-align: left; white-space: nowrap; }
  table.wt th span { color: #1A56A0; font-weight: 600; margin-left: 3px; }
  table.wt td { border: 1.5px solid #111; padding: 7px 7px; vertical-align: top; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  .ck { width: 46px; text-align: center; vertical-align: middle !important; }
  .box { display: inline-block; width: 26px; height: 26px; border: 2.5px solid #111; border-radius: 4px; font-size: 21px; line-height: 21px; font-weight: 900; text-align: center; }
  .no { width: 30px; text-align: center; color: #666; }
  td.nm { width: ${landscape ? '20%' : '34%'}; }
  .nm .ko { font-size: 18px; font-weight: 800; }
  .nm .zh { font-size: 15px; color: #1A56A0; font-weight: 700; }
  .nm .tp { font-size: 11px; color: #666; }
  .amt { width: 110px; text-align: right; white-space: nowrap; font-size: 18px; font-weight: 800; }
  .amt small { display: block; font-size: 12px; color: #666; font-weight: 500; }
  .ings { font-size: 13px; line-height: 1.5; }
  .ings span { display: inline-block; margin-right: 10px; white-space: nowrap; }
  .ings i { font-style: normal; color: #1A56A0; margin-left: 2px; }
  .ings b { font-weight: 800; margin-left: 3px; }
  .time { width: 70px; text-align: center; }
  .sign { width: 90px; text-align: center; }
  .nm .cat { display: inline-block; font-size: 11px; font-weight: 800; color: #1D4ED8; border: 1.5px solid #1D4ED8; border-radius: 5px; padding: 0 5px; margin-bottom: 2px; }
  .nm .note { font-size: 12.5px; color: #333; margin-top: 4px; white-space: pre-wrap; line-height: 1.45; border-left: 3px solid #ccc; padding-left: 6px; }
  .who { width: 120px; font-size: 13px; line-height: 1.45; }
  .who b { display: block; font-size: 15px; font-weight: 800; }
  .who .due { color: #111; font-weight: 700; }
  .who .due span { display: block; color: #1A56A0; font-weight: 600; font-size: 12px; }
  .memo { margin-top: 12px; border: 2px solid #111; border-radius: 8px; padding: 8px 12px; font-size: 15px; min-height: 80px; white-space: pre-wrap; }
  .memo .nt { font-size: 13px; font-weight: 700; color: #555; }
  .memo .nt span { color: #1A56A0; margin-left: 4px; }
  footer { font-size: 11px; color: #999; text-align: right; padding: 0 8px 12px; }
  @media print { .bar { display: none; } }
`;

/**
 * 작업표 인쇄
 * @param {object} opts
 *   items      [{ id, kind, recipeId, name, name_zh, grams, mode, text, note, owner, due, from, done, at, by }]
 *   recipesById Map(id → recipe)
 *   zhMap      단가표 기반 한→중 맵
 *   date       'YYYY-MM-DD'
 *   memo, storeName
 *   withIngredients 재료 칸 넣기 (기본 넣음 — 넣으면 가로 방향)
 * @returns {boolean} 창을 열었는지 (클릭 안에서 동기로 불러야 팝업 차단에 안 걸린다)
 */
export function printWorkList({ items = [], recipesById = new Map(), zhMap = new Map(), date = '', memo = '', storeName = '', withIngredients = true } = {}) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return false;
  // V155: 담당·기한이 하나라도 있으면 칸을 넣는다
  const withWho = list.some(it => it.owner || it.due || it.dueTime);
  const withAmt = list.some(it => it.kind === 'recipe');   // 직접 적은 일·회의 주제뿐이면 '만들 양' 칸은 뺀다
  const withIngs = withIngredients && withAmt;
  const win = window.open('', '_blank');
  if (!win) return false;

  const rows = list.map((it, idx) => {
    const r = it.kind === 'recipe' ? recipesById.get(it.recipeId) : null;
    const name = it.kind === 'text' ? it.text : (r?.name || it.name);
    const zh = it.kind === 'text' ? '' : (String(r?.name_zh || it.name_zh || '').trim());
    const tp = r ? (r.type === 'semi' ? '반제품 半成品' : r.type === 'menu' ? '메뉴 菜品' : '') : '';
    let amt = '';
    let ings = '';
    if (it.kind === 'recipe') {
      const g = Number(it.grams) || 0;
      const sv = Number(r?.serving_g) || 0;
      amt = `${esc(fmtAmount(g, 'g'))}${sv > 0 ? `<small>≈ ${esc(fmtAmount(g / sv, '인분'))} · ${esc(fmtAmount(g / sv, '份'))}</small>` : ''}`;
      if (withIngredients && r && recipeBase(r) > 0) {
        ings = scaleIngredients(r, g).map(ing => {
          const z = zhOf(zhMap, ing.name);
          return `<span>${esc(ing.name)}${z ? `<i>${esc(z)}</i>` : ''}<b>${esc(fmtAmount(ing.qty, ing.unit))}</b></span>`;
        }).join('');
      }
    }
    const cat = it.from ? `<div class="cat">🗓 회의 会议${it.from.title ? ` · ${esc(it.from.title)}` : ''}</div>` : '';
    const note = it.note ? `<div class="note">${esc(it.note)}</div>` : '';
    const dKo = fmtDue(it, 'ko', date); const dZh = fmtDue(it, 'zh', date);
    const who = `${it.owner ? `<b>${esc(it.owner)}</b>` : ''}${dKo ? `<div class="due">⏰ ${esc(dKo)}${dZh && dZh !== dKo ? `<span>${esc(dZh)}</span>` : ''}</div>` : ''}`;
    return `<tr>
      <td class="ck"><span class="box">${it.done ? '✓' : ''}</span></td>
      <td class="no">${idx + 1}</td>
      <td class="nm">${cat}<div class="ko">${esc(name)}</div>${zh ? `<div class="zh">${esc(zh)}</div>` : ''}${tp ? `<div class="tp">${tp}</div>` : ''}${note}</td>
      ${withAmt ? `<td class="amt">${amt}</td>` : ''}
      ${withWho ? `<td class="who">${who}</td>` : ''}
      ${withIngs ? `<td class="ings">${ings}</td>` : ''}
      <td class="time">${it.done ? esc(hhmm(it.at)) : ''}</td>
      <td class="sign">${it.done ? esc(it.by || '') : ''}</td>
    </tr>`;
  }).join('');

  const done = list.filter(i => i.done).length;
  win.document.open();
  win.document.write(`<!doctype html><html lang="ko"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>오늘의 작업 今日工作</title><style>${css(withIngs)}</style></head><body>
<div class="bar"><span>오늘의 작업 今日工作 · ${list.length}</span>
<button onclick="window.print()">🖨 인쇄 · 打印</button></div>
<main><header><h1>오늘의 작업<span>今日工作</span></h1>
<div class="meta">${esc(fmtDay(date, 'ko', true))} · ${esc(fmtDay(date, 'zh'))}${storeName ? ` · ${esc(storeName)}` : ''} · ${done}/${list.length}</div></header>
<table class="wt"><thead><tr>
  <th class="ck">완료<span>完成</span></th><th class="no">#</th><th>작업<span>工作</span></th>${withAmt ? '<th class="amt">만들 양<span>要做的量</span></th>' : ''}
  ${withWho ? '<th class="who">담당 · 마감<span>负责 · 截止</span></th>' : ''}
  ${withIngs ? '<th>재료<span>用料</span></th>' : ''}
  <th class="time">시간<span>时间</span></th><th class="sign">이름<span>签名</span></th>
</tr></thead><tbody>${rows}</tbody></table>
<div class="memo"><div class="nt">메모<span>备注</span></div>${esc(memo || '')}</div>
</main>
<footer>셈핏 SEMFIT</footer>
</body></html>`);
  win.document.close();
  return true;
}

export default printWorkList;
